import { useCallback } from 'react'
import type { ShapeCommittedUndoMeta } from '@/lib/shapes/types/shape-committed-undo-meta'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { GROUP_KIND_ID, isGroupShape } from '@/lib/groups/group-kind'
import { collectNestedGroupIdsPostOrder } from '@/lib/groups/shape-group-tree'
import {
  computeGroupBoundsFromDirectChildren,
  unionCanvasAabbFromShapeObbs,
} from '@/lib/groups/shape-group-bounds'
import { isLinkedTextFrame } from '@/lib/groups/linked-text-group-policy'
import { applyPartialShapeUpdate, getShapeById } from '../helpers/shape-list'
import { isShapeSelectable, normalizeSelectableIdsForScope } from '../helpers/selection-scope'
import type { ShapeManagerStateApi } from './use-shape-manager-state'
import type { SelectionHistoryApi } from './use-selection-history'
import type { EditScopeRefsApi } from './use-edit-scope-refs'

export function useShapeGrouping(
  ctx: ShapeManagerStateApi & SelectionHistoryApi & EditScopeRefsApi
) {
  const {
    editScopeGroupIdRef,
    getLinkedTextChainsRef,
    onLinkedTextExcludedFromGroupRef,
    onShapeCommitted,
    onShapeCreated,
    onUserEdit,
    recordShapeSelectionHistory,
    selectedShapeIdsRef,
    setSelectedShapeIds,
    setShapeBounds,
    setShapes,
    shapesRef,
  } = ctx

  const canGroupSelectedObjects = useCallback(() => {
    const shapes = shapesRef.current
    const rootIds = normalizeSelectableIdsForScope(
      selectedShapeIdsRef.current,
      shapes,
      editScopeGroupIdRef.current
    )

    if (rootIds.length < 2) return false
    if (rootIds.some((id) => getShapeById(shapes, id)?.locked === true)) return false
    return !rootIds.some((id) => getShapeById(shapes, id)?.frozen === true)
  }, [editScopeGroupIdRef, selectedShapeIdsRef, shapesRef])

  const groupSelectedObjects = useCallback((): ShapeData | null => {
    const list = shapesRef.current
    const selectedRootIdsAll = normalizeSelectableIdsForScope(
      selectedShapeIdsRef.current,
      list,
      editScopeGroupIdRef.current
    )

    if (selectedRootIdsAll.some((id) => getShapeById(list, id)?.locked === true)) return null
    if (selectedRootIdsAll.some((id) => getShapeById(list, id)?.frozen === true)) return null

    // Linked text frames are never pulled into a group — a group re-parents its children and
    // owns their geometry, which breaks the chain's text flow (WMPCM-6948 / WMPCM-7988). Drop
    // them here and group whatever is left; the caller toasts off `onLinkedTextExcludedFromGroup`.
    const linkedChains = getLinkedTextChainsRef.current?.() ?? []
    const excludedLinkedFrameIds = selectedRootIdsAll.filter((id) => {
      const shape = getShapeById(list, id)

      return shape != null && isLinkedTextFrame(shape, linkedChains)
    })
    const selectedRootIds = selectedRootIdsAll.filter((id) => !excludedLinkedFrameIds.includes(id))

    const selectedSet = new Set(selectedRootIds)
    const selectedRoots = list.filter(
      (shape) => selectedSet.has(shape.id) && isShapeSelectable(shape, editScopeGroupIdRef.current)
    )

    if (selectedRoots.length < 2) {
      // Excluding the linked frames is what left too little to group — say so rather than
      // failing silently, which is indistinguishable from a dead menu item.
      if (excludedLinkedFrameIds.length > 0) {
        onLinkedTextExcludedFromGroupRef.current?.({
          excludedLinkedFrameIds,
          grouped: false,
        })
      }

      return null
    }

    const union = unionCanvasAabbFromShapeObbs(selectedRoots)

    if (!union) return null

    // Z-order policy (locked): children keep their relative order in the shape list; the kind-7
    // group row owns canvas stacking for the unit. Insert the group **after** the topmost
    // selected member's index so the container paints above every child (splice inserts before
    // the given index, so topIndex alone would leave the last child above the group chrome).
    const insertIndex =
      Math.max(
        ...selectedRoots.map((shape) => list.findIndex((candidate) => candidate.id === shape.id)),
        -1
      ) + 1
    const groupId = `group-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    const historyTxnId = `group:${groupId}`
    const group: ShapeData = {
      id: groupId,
      type: 'square',
      kindId: GROUP_KIND_ID,
      x: union.x,
      y: union.y,
      width: union.width,
      height: union.height,
      rotation: 0,
      fill: 'transparent',
      stroke: 'transparent',
      strokeWidth: 0,
      opacity: 1,
    }

    const childBeforeById = new Map(
      selectedRoots.map((shape) => [shape.id, { ...shape, parentGroupId: shape.parentGroupId }])
    )

    setShapes((prev) => {
      const next = prev.map((shape) =>
        selectedSet.has(shape.id) ? { ...shape, parentGroupId: groupId } : shape
      )

      next.splice(insertIndex < 0 ? next.length : insertIndex, 0, group)
      shapesRef.current = next
      return next
    })

    const nextSelection = [groupId]

    selectedShapeIdsRef.current = nextSelection
    setSelectedShapeIds(nextSelection)
    recordShapeSelectionHistory(selectedRootIds, nextSelection, {
      source: 'command',
      gesture: 'replace',
      historyTxnId,
    })
    setShapeBounds((prev) => ({
      ...prev,
      [groupId]: { x: 0, y: 0, width: group.width, height: group.height },
    }))

    onShapeCreated?.(group, { historyTxnId, historyInsertIndex: insertIndex })
    onUserEdit?.([groupId, ...selectedRootIds])

    const nextShapes = shapesRef.current

    for (const childId of selectedRootIds) {
      const beforeShape = childBeforeById.get(childId)
      const afterShape = nextShapes.find((shape) => shape.id === childId)

      if (!beforeShape || !afterShape) continue
      onShapeCommitted?.(afterShape, 'update', {
        before: { parentGroupId: beforeShape.parentGroupId },
        after: { parentGroupId: groupId },
        historyTxnId,
      })
    }

    if (excludedLinkedFrameIds.length > 0) {
      onLinkedTextExcludedFromGroupRef.current?.({ excludedLinkedFrameIds, grouped: true })
    }

    return group
  }, [
    onShapeCommitted,
    onShapeCreated,
    onUserEdit,
    recordShapeSelectionHistory,
    editScopeGroupIdRef,
    getLinkedTextChainsRef,
    onLinkedTextExcludedFromGroupRef,
    selectedShapeIdsRef,
    setSelectedShapeIds,
    setShapeBounds,
    setShapes,
    shapesRef,
  ])

  const recomputePassiveGroupBounds = useCallback(
    (groupId: string, opts?: { includeNested?: boolean }) => {
      const includeNested = opts?.includeNested ?? true
      const prev = shapesRef.current
      const target = prev.find((shape) => shape.id === groupId)

      if (!target || !isGroupShape(target)) return

      const order = includeNested ? collectNestedGroupIdsPostOrder(prev, groupId) : [groupId]
      let next = prev
      const commits: { id: string; before: ShapeData }[] = []

      for (const id of order) {
        const group = next.find((shape) => shape.id === id)

        if (!group || !isGroupShape(group)) continue
        const bounds = computeGroupBoundsFromDirectChildren(next, id, group)

        if (!bounds) continue
        const epsilon = 1e-4
        const hasChange =
          Math.abs(group.x - bounds.x) > epsilon ||
          Math.abs(group.y - bounds.y) > epsilon ||
          Math.abs(group.width - bounds.width) > epsilon ||
          Math.abs(group.height - bounds.height) > epsilon

        if (!hasChange) continue

        commits.push({ id, before: { ...group } })
        next = applyPartialShapeUpdate(next, id, {
          x: bounds.x,
          y: bounds.y,
          width: bounds.width,
          height: bounds.height,
        })
      }

      if (commits.length === 0) return

      shapesRef.current = next
      setShapes(next)

      const historyTxnId = `passive-group-bounds:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`
      const nextById = new Map(next.map((s) => [s.id, s]))

      for (const c of commits) {
        const afterShape = nextById.get(c.id)

        if (!afterShape || !isGroupShape(afterShape)) continue

        const undoBefore: ShapeCommittedUndoMeta['before'] = {
          x: c.before.x,
          y: c.before.y,
          width: c.before.width,
          height: c.before.height,
        }
        const undoAfter: ShapeCommittedUndoMeta['after'] = {
          x: afterShape.x,
          y: afterShape.y,
          width: afterShape.width,
          height: afterShape.height,
        }

        if (typeof c.before.rotation === 'number') {
          undoBefore.rotation = c.before.rotation
        }

        if (typeof afterShape.rotation === 'number') {
          undoAfter.rotation = afterShape.rotation
        }

        onShapeCommitted?.(afterShape, 'resize', {
          before: undoBefore,
          after: undoAfter,
          historyTxnId,
        })
      }
    },
    [onShapeCommitted, setShapes, shapesRef]
  )

  return { canGroupSelectedObjects, groupSelectedObjects, recomputePassiveGroupBounds }
}

export type ShapeGroupingApi = ReturnType<typeof useShapeGrouping>
